/**
 * Wall thickness for the plan view.
 *
 * The chain the designer draws and measures is the wall's FRONT (interior)
 * face — its lengths are the clear room dimensions (owner, 2026-07-24; ADR
 * 0020). A real wall has thickness, so on the plan each wall is drawn as a
 * band extending OUTWARD from that front line to an exterior (back) face,
 * leaving the interior footprint exactly as typed.
 *
 * The interesting consequence the owner called out: at a corner the two back
 * faces meet further out than the front faces, so **each wall's back edge is
 * longer than its front edge**. That falls straight out of a mitered offset —
 * the outer vertices are the intersections of the neighbouring offset lines,
 * not a fixed extension — so it is correct for any angle, not just 90°.
 *
 * This is a RENDER layer only. The closure solver still works on the front
 * polygon; nothing here feeds back into geometry. Coordinates are whatever the
 * caller uses (the app passes canvas px), and `thicknessFor` returns thickness
 * in those same units, so the module is unit-agnostic.
 */

/** Shoelace signed area; sign tells winding (matches chain.js: + = clockwise on screen). */
function signedArea(vertices) {
  let sum = 0;
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

function unit(dx, dy) {
  const len = Math.hypot(dx, dy);
  return len < 1e-9 ? { x: 0, y: 0 } : { x: dx / len, y: dy / len };
}

/**
 * Intersection of infinite lines (p1 + a·d1) and (p2 + b·d2), or null when
 * they are parallel (collinear neighbours — e.g. the two walls flanking a
 * door). The caller falls back to the plain offset point there.
 */
function lineIntersect(p1, d1, p2, d2) {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 1e-9) return null;
  const a = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + a * d1.x, y: p1.y + a * d1.y };
}

/**
 * Wall bands for the plan: one filled quad per WALL segment, mitered at both
 * ends so corners meet cleanly and back edges run long.
 *
 * Openings (doors) get no band — the doorway is a gap — but they still take
 * part in the offset so the walls on either side miter against the right
 * geometry. An opening carries its neighbours' wall thickness (a door is cut
 * into a wall of that thickness), which `thicknessFor` supplies.
 *
 * @param {Array<{id:string,start:{x,y},end:{x,y},segmentKind?:string}>} chain
 *        the closed, connected front-face loop (walls + openings), in order.
 * @param {object} opts
 * @param {(segment)=>number} opts.thicknessFor thickness for a segment, in the
 *        chain's coordinate units. Should be > 0.
 * @param {Set<string>} [opts.partitionIds] ids of `opening` segments that are
 *        interior-wall footprints (ADR 0032): the boundary must stay SOLID
 *        through them, so they get a band like a wall does rather than a gap.
 * @returns {Array<{id:string, points:Array<{x,y}>}>} one entry per wall segment,
 *          `points` a 4-vertex polygon [frontStart, frontEnd, backEnd, backStart].
 */
export function wallBands(chain, { thicknessFor, partitionIds } = {}) {
  const n = chain?.length ?? 0;
  if (n < 3 || typeof thicknessFor !== 'function') return [];

  // Winding from the loop of segment starts (+1 = clockwise on screen). Only
  // used to orient the outward normal away from the interior.
  const winding = signedArea(chain.map((s) => s.start)) >= 0 ? 1 : -1;

  // Each segment's OWN direction and OUTWARD normal, from its own start→end —
  // NOT from vertex[i]→vertex[i+1], which is only the same thing on a CLOSED
  // loop. Using the segment's own endpoints is what makes an open chain (the
  // reported sliver) offset each wall along itself instead of across the gap.
  const dir = [];
  const normal = [];
  for (let i = 0; i < n; i++) {
    const d = unit(chain[i].end.x - chain[i].start.x, chain[i].end.y - chain[i].start.y);
    dir.push(d);
    normal.push({ x: d.y * winding, y: -d.x * winding });
  }

  const thick = chain.map((seg) => Math.max(0, thicknessFor(seg) || 0));

  // How far a mitered corner may run out, as a multiple of the local
  // thickness. At an acute interior corner the two offset lines meet far
  // outside the wall (miter length = thickness / sin(halfAngle) → ∞ as the
  // angle closes), which without a limit paints a huge spike across the room.
  // Beyond the limit the corner is clamped back — a bevel — as CAD offsets do.
  // 90° corners (miter ≈ 1.41×) stay well under the limit and remain sharp.
  const MITER_LIMIT = 4;

  // A point on edge i's offset line (its start, pushed out by its thickness).
  const offPoint = (i) => ({ x: chain[i].start.x + normal[i].x * thick[i], y: chain[i].start.y + normal[i].y * thick[i] });

  /** True when edges a and b actually share a corner (an endpoint in common). */
  function connected(a, b) {
    const ends = [chain[a].start, chain[a].end];
    const others = [chain[b].start, chain[b].end];
    const eps = 2 * Math.max(thick[a], thick[b], 1);
    let min = Infinity;
    for (const p of ends) for (const q of others) min = Math.min(min, Math.hypot(p.x - q.x, p.y - q.y));
    return min <= eps;
  }

  /**
   * The back corner of wall edge `a` at its front vertex `vertex`, mitered
   * against neighbour edge `b`. Miters only when the two edges actually touch —
   * an open end (where the far side of the loop is not really adjacent) squares
   * off instead of drawing a false miter across the opening (the reported gray
   * fan). Collinear neighbours (a door jamb) also square off. Runaway acute
   * miters are clamped to MITER_LIMIT × thickness.
   */
  function backCorner(vertex, a, b) {
    const plain = { x: vertex.x + normal[a].x * thick[a], y: vertex.y + normal[a].y * thick[a] };
    if (!connected(a, b)) return plain;
    const hit = lineIntersect(offPoint(a), dir[a], offPoint(b), dir[b]);
    if (!hit) return plain;
    const mdx = hit.x - vertex.x;
    const mdy = hit.y - vertex.y;
    const mlen = Math.hypot(mdx, mdy);
    const limit = MITER_LIMIT * Math.max(thick[a], thick[b]);
    return mlen > limit && mlen > 1e-9
      ? { x: vertex.x + (mdx / mlen) * limit, y: vertex.y + (mdy / mlen) * limit }
      : hit;
  }

  const isSolid = (i) => (chain[i].segmentKind ?? 'wall') === 'wall' || (partitionIds && partitionIds.has(chain[i].id));

  const bands = [];
  for (let i = 0; i < n; i++) {
    if (!isSolid(i)) continue; // door openings are gaps; partition footprints are solid
    const frontStart = { x: chain[i].start.x, y: chain[i].start.y };
    const frontEnd = { x: chain[i].end.x, y: chain[i].end.y };
    // backStart mitered against the previous edge; backEnd against the next.
    const backStart = backCorner(frontStart, i, (i - 1 + n) % n);
    const backEnd = backCorner(frontEnd, i, (i + 1) % n);
    bands.push({ id: chain[i].id, points: [frontStart, frontEnd, backEnd, backStart] });
  }
  return bands;
}
